<template>
  <div>
    <div style="clear:both">
      <div style="float:left;">
        <editor-button
          icon-only
          button-icon="el-icon-edit"
          :disabled="!canEditPrice()"
          :loading="loading"
          button-translation="common.edit"
          action="primary"
          @click="editPrice()"
        />
      </div>
      <el-select
        v-model="selectedPriceTypeOption"
        :disabled="loading"
        :clearable="true"
        class="search-select"
        type="select"
        style="float:left;margin-bottom:10px;margin-left:10px;"
        :placeholder="$t('priceListItemPrice.priceType')"
        @change="handleFilterChange"
      >
        <el-option
          v-for="priceTypeOption in priceTypeOptions"
          :key="priceTypeOption.code"
          :label="priceTypeOption.description"
          :value="priceTypeOption.code"
        />
      </el-select>
      <date-picker
        v-model="validDate"
        :disabled="loading"
        :show-week-numbers="false"
        :placeholder="$t('common.date')"
        style="float:left;margin-bottom:10px;margin-left:10px"
        class="search-select"
        @input="handleFilterChange"
      />
      <el-select
        v-if="showCountrySelection()"
        v-model="selectedCountryID"
        :disabled="loading"
        clearable
        class="search-select"
        type="select"
        style="float:left;margin-bottom:10px;margin-left:10px"
        :placeholder="$t('common.country')"
        @input="handleFilterChange"
      >
        <el-option v-for="country in countries" :key="country.id" :label="country.name" :value="country.id" />
      </el-select>
      <el-select
        v-if="showCultureSelection()"
        v-model="selectedCultureID"
        :disabled="loading"
        clearable
        class="search-select"
        type="select"
        style="float:left;margin-bottom:10px;margin-left:10px"
        :placeholder="$t('domain.culture')"
        @change="handleFilterChange"
      >
        <el-option v-for="culture in cultures" :key="culture.id" :label="culture.description" :value="culture.id" />
      </el-select>
      <div v-if="matrixYN" style="float:left;text-align:center;margin-left:10px">
        <el-select
          v-model="selectedWidth"
          :disabled="loading"
          class="search-select"
          type="select"
          :placeholder="$t('priceListItem.width')"
          @input="dimensionInput"
        >
          <el-option
            v-for="width in widths"
            :key="width"
            :label="$t('priceListItem.width') + ': ' + width"
            :value="width"
          />
        </el-select>
        <el-select
          v-model="selectedHeight"
          :disabled="loading"
          class="search-select"
          type="select"
          :placeholder="$t('priceListItem.height')"
          style="margin-left:10px"
          @input="dimensionInput"
        >
          <el-option
            v-for="height in heights"
            :key="height"
            :label="$t('priceListItem.height') + ': ' + height"
            :value="height"
          />
        </el-select>
      </div>
    </div>
    <div ref="tableContainer">
      <pagination
        :page-sizes="[25, 50, 100]"
        :total="loadedData.total"
        :page.sync="page"
        :limit.sync="pageSize"
        style="clear:both"
        @pagination="refreshList"
      >
      </pagination>
      <el-table :data="items" border row-key="priceListItemPriceID" fit :max-height="getMaxTableHeight()">
        <el-table-column :label="$t('common.startDate')" prop="startDate" width="100px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['startDate']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.endDate')" prop="endDate" width="100px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['endDate']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('priceListItemPrice.priceType')" prop="priceType" width="140px">
          <template slot-scope="scope">
            <span>{{ formatPriceType(scope.row['priceType']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.price')" width="80px" align="right">
          <template slot-scope="scope">
            <span>{{ formatPrice(scope.row['price']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.currency')" width="100px" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row['currencyCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.country')" prop="countryName" width="150">
          <template slot-scope="scope">
            <span>{{ scope.row['countryName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('domain.culture')" prop="cultureDescription">
          <template slot-scope="scope">
            <span>{{ scope.row['cultureDescription'] }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/DSE/DatePicker';
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import { PriceTypeOption } from '@/utils/constants';
import { toDateTimeString } from '@/utils/date';

export default {
  components: {
    DatePicker,
    EditorButton,
    Pagination
  },
  props: {
    widths: {
      default: () => [],
      type: Array
    },
    heights: {
      default: () => [],
      type: Array
    }
  },
  data() {
    return {
      loading: false,
      loadedData: { items: [], total: 0 },
      matrixEntries: [],
      page: 1,
      pageSize: 25,
      selectedCountryID: null,
      selectedCultureID: null,
      selectedHeight: null,
      selectedPriceTypeOption: null,
      selectedWidth: null,
      validDate: null
    };
  },
  computed: {
    countries() {
      return this.$store.getters['country/loadedItems'];
    },
    cultures() {
      return this.$store.getters['culture/loadedItems'];
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    items() {
      return this.loadedData.items;
    },
    listParams() {
      const params = {
        priceListItemID: this.priceListItemID,
        priceListMatrixID: this.priceListMatrixID,
        priceTypeOption: this.selectedPriceTypeOption,
        page: this.page,
        pageSize: this.pageSize,
        validDate: this.validDate
      };
      if (this.showCountrySelection() && this.selectedCountryID) {
        params.countryID = this.selectedCountryID;
      }
      if (this.showCultureSelection() && this.selectedCultureID) {
        params.cultureID = this.selectedCultureID;
      }
      return params;
    },
    matrixYN() {
      const matrixYN = this.$route.query.matrixYN;
      if (typeof matrixYN === 'string') {
        return matrixYN === 'true' || matrixYN === 'TRUE';
      }
      return matrixYN; // boolean
    },
    priceListItemID() {
      return +this.$route.params.id;
    },
    priceListMatrixID() {
      const width = this.selectedWidth;
      const height = this.selectedHeight;
      if (width !== null && height !== null) {
        const entry = this.matrixEntries.find(elem => elem.widthInCm === width && elem.heightInCm === height);
        return entry && entry.priceListMatrixID;
      }
      return null;
    },
    priceTypeOptions() {
      return this.$store.getters['option/loadedOptions']('priceType');
    }
  },
  created() {
    const matrixYN = this.matrixYN;
    if (matrixYN) {
      this.getMatrixEntries();
    } else {
      this.refreshList();
    }
  },
  methods: {
    clearLoadedData() {
      this.loadedData.items = [];
      this.loadedData.total = 0;
    },
    canEditPrice() {
      const matrixYN = this.matrixYN;
      return !matrixYN || !!this.priceListMatrixID;
    },
    dimensionInput() {
      if (this.priceListMatrixID) {
        this.refreshList();
      } else {
        this.clearLoadedData();
      }
    },
    editPrice() {
      const route = {
        name: 'EditPriceListItemPrice',
        params: {
          priceListItemID: this.priceListItemID,
          priceListMatrixID: this.priceListMatrixID
        }
      };
      this.$router.push(route);
    },
    handleFilterChange() {
      this.refreshList();
    },
    formatDate(cellValue) {
      return cellValue && toDateTimeString(cellValue, 'DD-MM-YYYY');
    },
    formatPrice(cellValue) {
      if (isNaN(cellValue)) {
        return '';
      }
      return `${cellValue.toFixed(2)}`;
    },
    formatPriceType(cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('priceType').find(item => item.code === cellValue);
      return option && option.description;
    },
    getMatrixEntries() {
      this.loading = true;
      this.$store
        .dispatch('priceListItem/getMatrix', {
          priceListItemID: this.priceListItemID
        })
        .then(data => {
          this.matrixEntries = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    },
    refreshList() {
      this.loading = true;
      this.$store
        .dispatch('priceListItem/getPrices', this.listParams)
        .then(data => {
          this.loadedData.items = data.items;
          this.loadedData.total = data.total;
        })
        .catch(() => {
          this.clearLoadedData();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showCountrySelection() {
      return this.selectedPriceTypeOption === PriceTypeOption.WorkshopPrice;
    },
    showCultureSelection() {
      return (
        this.selectedPriceTypeOption === PriceTypeOption.ConsumerPrice ||
        this.selectedPriceTypeOption === PriceTypeOption.PurchasePrice
      );
    }
  }
};
</script>
